import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const ENDPOINT = '/api/helpdesk/settings/notification-emails';

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function HelpdeskNotificationEmails() {
  const [input, setInput] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ emails: string[] }>({
    queryKey: ['helpdesk-notification-emails'],
    queryFn: () => apiRequest('GET', ENDPOINT) as Promise<{ emails: string[] }>,
  });

  const emails = data?.emails ?? [];

  const { mutate: save, isPending } = useMutation({
    mutationFn: (updated: string[]) => apiRequest('PUT', ENDPOINT, { emails: updated }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['helpdesk-notification-emails'] });
      toast({ title: 'Saved', description: 'Notification emails updated.' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to save.', variant: 'destructive' }),
  });

  const add = () => {
    const trimmed = input.trim().toLowerCase();
    if (!isValidEmail(trimmed)) return toast({ title: 'Invalid email', variant: 'destructive' });
    if (emails.includes(trimmed)) return toast({ title: 'Already added', variant: 'destructive' });
    save([...emails, trimmed]);
    setInput('');
  };

  const remove = (email: string) => save(emails.filter(e => e !== email));

  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); add(); } };

  return (
    <div className="p-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Ticket Notification Emails</CardTitle>
          <CardDescription>These addresses will be notified when a support ticket is created or updated by a user.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter email address"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isPending}
            />
            <Button onClick={add} disabled={isPending || !input.trim()}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : emails.length === 0 ? (
            <p className="text-sm text-muted-foreground">No notification emails configured.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {emails.map(email => (
                <Badge key={email} variant="secondary" className="flex items-center gap-1 pr-1 text-sm">
                  {email}
                  <button onClick={() => remove(email)} className="ml-1 hover:text-destructive" disabled={isPending}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
